import { useMemo, useState } from "react";
import {
  CalendarClock,
  CheckCircle2,
  Circle,
  ClipboardList,
  Lightbulb,
  Plus,
  RotateCcw,
  Search,
  Trash2,
} from "lucide-react";

const STORAGE_KEY = "notaris_reminders";

const defaultForm = {
  title: "",
  note: "",
  category: "Berkas",
  status: "Tertunda",
  priority: "Normal",
  dueDate: "",
  pic: "",
  clientName: "",
};

const defaultLegacyItems = [
  { id: 1, text: "Dr Aditya (Batu)", done: false },
  { id: 2, text: "ELIZABET CS", done: false },
  { id: 3, text: "Miftakhul Anas", done: false },
  { id: 4, text: "Ayu & Diki (Dilem) - D", done: false },
  { id: 5, text: "MUDIONO (Kalipare)", done: false },
  { id: 6, text: "Toni/Bu Mardani -> Pihak blm bayar", done: false },
];

const fieldClass =
  "w-full rounded-lg border c-border bg-white px-3 py-2.5 text-sm text-ink placeholder:text-[#7A828A] outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/20 dark:bg-[#10171E] dark:text-[#ECE9E2] dark:placeholder:text-[#9AA1A8]";

const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.16em] c-muted";

const statuses = ["Tertunda", "Proses", "Menunggu", "Selesai"];
const categories = ["Berkas", "Pembayaran", "TTD", "Validasi", "Pajak", "Sertifikat", "Lainnya"];
const priorities = ["Rendah", "Normal", "Penting", "Mendesak"];

const normalizeReminder = (item) => ({
  id: item.id || crypto.randomUUID(),
  title: item.title || item.text || "",
  note: item.note || "",
  category: item.category || "Berkas",
  status: item.status || (item.done ? "Selesai" : "Tertunda"),
  priority: item.priority || "Normal",
  dueDate: item.dueDate || "",
  pic: item.pic || "",
  clientName: item.clientName || "",
  createdAt: item.createdAt || new Date().toISOString(),
});

const formatDate = (value) => {
  if (!value) return "Tanpa target";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "long", year: "numeric" }).format(date);
};

const statusClass = (status) => {
  if (status === "Selesai") return "bg-emerald2/10 text-emerald2 dark:text-[#7FBF9F]";
  if (status === "Proses") return "bg-blue-500/10 text-[#55BDEB]";
  if (status === "Menunggu") return "bg-gold/10 text-gold";
  return "bg-wine/10 text-wine dark:text-[#E08585]";
};

const priorityClass = (priority) => {
  if (priority === "Mendesak") return "border-wine/30 bg-wine/10 text-wine dark:text-[#E08585]";
  if (priority === "Penting") return "border-gold/30 bg-gold/10 text-gold";
  return "c-border c-text";
};

export default function Pengingat() {
  const [form, setForm] = useState(defaultForm);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Semua");
  const [items, setItems] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      return (saved || defaultLegacyItems).map(normalizeReminder);
    } catch {
      return defaultLegacyItems.map(normalizeReminder);
    }
  });

  const saveItems = (next) => {
    setItems(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const stats = useMemo(
    () => ({
      total: items.length,
      pending: items.filter((item) => item.status !== "Selesai").length,
      process: items.filter((item) => item.status === "Proses").length,
      done: items.filter((item) => item.status === "Selesai").length,
    }),
    [items]
  );

  const filteredItems = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return items.filter((item) => {
      const matchesStatus = statusFilter === "Semua" || item.status === statusFilter;
      const haystack = [item.title, item.note, item.category, item.priority, item.pic, item.clientName].join(" ").toLowerCase();
      return matchesStatus && (!keyword || haystack.includes(keyword));
    });
  }, [items, query, statusFilter]);

  const updateField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const addReminder = (event) => {
    event.preventDefault();
    if (!form.title.trim()) return;
    const reminder = {
      ...form,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
    };
    saveItems([reminder, ...items]);
    setForm(defaultForm);
  };

  const updateStatus = (id, status) => {
    saveItems(items.map((item) => (item.id === id ? { ...item, status } : item)));
  };

  const deleteReminder = (id) => {
    saveItems(items.filter((item) => item.id !== id));
  };

  return (
    <div className="mx-auto max-w-[96rem] space-y-6 p-4 pt-16 sm:p-8 lg:p-10">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-2 text-[11px] uppercase tracking-[0.3em] text-gold">Pengingat</div>
          <h1 className="font-display text-3xl font-medium tracking-tight c-strong sm:text-4xl">Pengingat Berkas</h1>
          <p className="mt-2 text-sm c-muted">
            Catat pekerjaan yang tertunda, sedang proses, menunggu pihak lain, atau sudah selesai.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setForm(defaultForm)}
          className="inline-flex items-center gap-2 rounded-lg border c-border px-4 py-2.5 text-sm font-medium c-text transition hover:border-gold hover:text-gold"
        >
          <RotateCcw className="h-4 w-4" /> Reset Form
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Stat label="Total" value={stats.total} icon={ClipboardList} />
        <Stat label="Belum Selesai" value={stats.pending} icon={Circle} />
        <Stat label="Dalam Proses" value={stats.process} icon={CalendarClock} />
        <Stat label="Selesai" value={stats.done} icon={CheckCircle2} />
      </div>

      <div className="grid gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
        <form onSubmit={addReminder} className="rounded-xl border c-border c-card p-5 card-elev xl:sticky xl:top-6 xl:self-start">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-gold/10 text-gold">
              <Lightbulb className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-medium c-strong">Tambah Pengingat</h2>
              <p className="mt-1 text-xs c-muted">Judul wajib diisi, detail lain bisa menyusul.</p>
            </div>
          </div>

          <div className="space-y-4">
            <Field label="Judul">
              <input className={fieldClass} value={form.title} onChange={(e) => updateField("title", e.target.value)} placeholder="Mis. Toni/Bu Mardani belum bayar" required />
            </Field>
            <Field label="Catatan">
              <textarea className={`${fieldClass} min-h-[110px]`} value={form.note} onChange={(e) => updateField("note", e.target.value)} placeholder="Tambahkan detail, kendala, dokumen kurang, nomor kontak, atau langkah berikutnya" />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Status">
                <select className={fieldClass} value={form.status} onChange={(e) => updateField("status", e.target.value)}>
                  {statuses.map((status) => <option key={status}>{status}</option>)}
                </select>
              </Field>
              <Field label="Prioritas">
                <select className={fieldClass} value={form.priority} onChange={(e) => updateField("priority", e.target.value)}>
                  {priorities.map((priority) => <option key={priority}>{priority}</option>)}
                </select>
              </Field>
              <Field label="Kategori">
                <select className={fieldClass} value={form.category} onChange={(e) => updateField("category", e.target.value)}>
                  {categories.map((category) => <option key={category}>{category}</option>)}
                </select>
              </Field>
              <Field label="Target">
                <input type="date" className={fieldClass} value={form.dueDate} onChange={(e) => updateField("dueDate", e.target.value)} />
              </Field>
              <Field label="Client">
                <input className={fieldClass} value={form.clientName} onChange={(e) => updateField("clientName", e.target.value)} placeholder="Nama client/pihak" />
              </Field>
              <Field label="PIC">
                <input className={fieldClass} value={form.pic} onChange={(e) => updateField("pic", e.target.value)} placeholder="Staff penanggung jawab" />
              </Field>
            </div>
          </div>

          <button type="submit" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gold px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-gold/20 transition hover:bg-gold-dark">
            <Plus className="h-4 w-4" /> Simpan Pengingat
          </button>
        </form>

        <div className="rounded-xl border c-border c-card p-5 card-elev">
          <div className="flex flex-col gap-3 border-b c-border pb-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-display text-2xl font-medium c-strong">Daftar Pengingat</h2>
              <p className="mt-1 text-xs c-muted">Ubah status cepat dari kartu pengingat.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 c-muted" />
                <input className={`${fieldClass} w-64 pl-9`} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari pengingat" />
              </div>
              <select className={`${fieldClass} w-40`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option>Semua</option>
                {statuses.map((status) => <option key={status}>{status}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-4 grid gap-3 xl:grid-cols-2">
            {filteredItems.length === 0 ? (
              <div className="rounded-lg border border-dashed c-border p-6 text-center text-sm c-muted xl:col-span-2">
                Tidak ada pengingat yang cocok.
              </div>
            ) : (
              filteredItems.map((item) => (
                <div key={item.id} className="rounded-lg border c-border c-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold c-strong">{item.title}</div>
                      <div className="mt-1 text-xs c-muted">{item.category} - {formatDate(item.dueDate)}</div>
                    </div>
                    <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${statusClass(item.status)}`}>{item.status}</span>
                  </div>

                  {item.note && <p className="mt-3 whitespace-pre-line text-sm c-text">{item.note}</p>}

                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className={`rounded-lg border px-2.5 py-1 ${priorityClass(item.priority)}`}>{item.priority}</span>
                    {item.clientName && <span className="rounded-lg border c-border px-2.5 py-1 c-muted">{item.clientName}</span>}
                    {item.pic && <span className="rounded-lg border c-border px-2.5 py-1 c-muted">PIC: {item.pic}</span>}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {statuses.map((status) => (
                      <button
                        key={status}
                        type="button"
                        onClick={() => updateStatus(item.id, status)}
                        className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${
                          item.status === status ? "border-gold bg-gold/10 text-gold" : "c-border c-text hover:border-gold hover:text-gold"
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                    <button type="button" onClick={() => deleteReminder(item.id)} className="ml-auto inline-flex items-center justify-center rounded-lg border border-wine/20 px-3 py-2 text-wine transition hover:bg-wine/10">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label>
      <span className={labelClass}>{label}</span>
      {children}
    </label>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg border c-border c-card p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold/10 text-gold">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs c-muted">{label}</div>
          <div className="text-xl font-semibold c-strong">{value}</div>
        </div>
      </div>
    </div>
  );
}
